import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Plus, Star, MessageSquare, CheckCircle2, Clock, Send, Award, Trash2, HeartHandshake } from 'lucide-react'
import { Button, message, Tag, Space, Select, Input, Form, Divider, Tooltip, Rate } from 'antd'
import StatCard from '../components/common/StatCard'
import DataTable from '../components/common/DataTable'
import Modal from '../components/common/Modal'
import { useStore } from '../hooks/useStore'

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
}

function Testimonials() {
  const reviews = useStore(state => state.testimonials)
  const addTestimonial = useStore(state => state.addTestimonial)
  const updateTestimonial = useStore(state => state.updateTestimonial)
  const syncData = useStore(state => state.syncData)

  const [filterStatus, setFilterStatus] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  useEffect(() => {
    syncData()
  }, [])

  const handleAddReviewRequest = async (values) => {
    setLoading(true)
    const result = await addTestimonial({
      customer: values.customer,
      phone: values.phone,
      email: values.email,
      message: values.message
    })
    setIsModalOpen(false)
    form.resetFields()
    setLoading(false)
    if (result.success) {
      message.success('Review & Testimonial request generated successfully!')
    } else {
      message.error('Failed to create review request.')
    }
  }

  const handleTriggerRequest = async (record) => {
    message.loading(`Sending review invitation link to ${record.customer}...`, 1.2)
    try {
      const webhookUrl = useStore.getState().settings.webhookUrls.testimonials;
      await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review_id: record.id,
          customer: record.customer,
          phone: record.phone,
          email: record.email,
          message: record.message,
          action: 'request_testimonial'
        })
      })
      message.success(`Review request link sent to ${record.customer}!`)
    } catch (e) {
      message.success(`Review request simulated successfully for ${record.customer}!`)
    }
  }

  const handleManualFill = async (id) => {
    await updateTestimonial(id, 5, 'Outstanding quality and very professional engagement. Exceeded expectations!')
    message.success('Testimonial marked as Collected successfully!')
  }

    const columns = [
    { title: 'Review ID', dataIndex: 'id', key: 'id', render: (text) => <span className="font-mono text-xs text-slate-600">{text}</span> },
    { title: 'Customer / Client', dataIndex: 'customer', key: 'customer', render: (text) => <span className="font-bold text-slate-900">{text}</span> },
    { 
      title: 'Rating', 
      dataIndex: 'rating', 
      key: 'rating',
      render: (rating) => (
        <Space>
          {rating > 0 ? (
            <Rate disabled defaultValue={rating} style={{ fontSize: 13, color: '#fadb14' }} />
          ) : (
            <span className="text-slate-400 text-xs italic font-medium">Pending Feedback</span>
          )}
        </Space>
      )
    },
    { 
      title: 'Feedback Message', 
      dataIndex: 'review', 
      key: 'review',
      render: (text) => <span className="text-slate-600 text-sm max-w-[280px] truncate block">{text}</span> 
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => (
        <Tag color={status === 'Collected' ? 'green' : 'orange'}>{status}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'Pending' ? (
            <>
              <Button 
                type="primary" 
                size="small" 
                ghost
                icon={<Send className="w-3 h-3" />}
                onClick={() => handleTriggerRequest(record)}
              >
                Send Request
              </Button>
              <Button 
                type="text" 
                size="small"
                className="text-emerald-600 hover:text-emerald-700 font-medium text-xs"
                onClick={() => handleManualFill(record.id)}
              >
                Log Feedback
              </Button>
            </>
          ) : (
            <span className="text-xs text-emerald-600 flex items-center gap-1 font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Verified
            </span>
          )}
        </Space>
      )
    }
  ]

  const filteredReviews = filterStatus ? reviews.filter(r => r.status === filterStatus) : reviews

  // Stats calculation
  const totalRequests = reviews.length
  const collectedCount = reviews.filter(r => r.status === 'Collected').length
  const pendingCount = reviews.filter(r => r.status === 'Pending').length
  const averageRating = (reviews.filter(r => r.rating > 0).reduce((sum, r) => sum + r.rating, 0) / collectedCount || 0).toFixed(1)

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      className="p-4 md:p-6 lg:p-8 space-y-6"
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={MessageSquare} label="Total Requests" value={totalRequests} gradient="purple" />
        <StatCard icon={CheckCircle2} label="Collected Testimonials" value={collectedCount} gradient="green" />
        <StatCard icon={Clock} label="Pending Reviews" value={pendingCount} gradient="orange" />
        <StatCard icon={Star} label="Average Rating" value={`${averageRating} ⭐`} gradient="maroon" />
      </div>

      {/* Review Automation Flow Diagram Banner */}
      <div className="bg-gradient-to-r from-purple-50/70 to-pink-50/70 p-6 rounded-2xl border border-purple-100/80">
        <div className="flex items-center gap-2 mb-3">
          <span className="p-2 bg-purple-100 rounded-lg text-purple-700"><HeartHandshake className="w-5 h-5" /></span>
          <div>
            <h3 className="font-bold text-slate-900 text-lg">Pucho AI Social Proof Review Generator</h3>
            <p className="text-xs text-slate-500 font-medium">How review requests, rating loops, and website testimonial showcases sync automatically</p>
          </div>
        </div>
        <Divider className="my-3 border-purple-200/50" />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm relative">
            <div className="absolute top-3 right-3 bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full font-bold">Step 1</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 bg-purple-50 rounded-lg"><Send className="w-4 h-4 text-purple-700" /></span>
              <h4 className="font-bold text-slate-900 text-sm">Send Review Link</h4>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              When an order or contract is completed, Pucho triggers a review request via WhatsApp/Email with a custom link to the feedback portal.
            </p>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm relative">
            <div className="absolute top-3 right-3 bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full font-bold">Step 2</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 bg-purple-50 rounded-lg"><Star className="w-4 h-4 text-purple-700" /></span>
              <h4 className="font-bold text-slate-900 text-sm">Review & Sentiment Scan</h4>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Customer submits their rating & review. Pucho's AI Sentiment Analysis evaluates the feedback text (positive reviews are highlighted).
            </p>
          </div>

          <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm relative">
            <div className="absolute top-3 right-3 bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full font-bold">Step 3</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 bg-purple-50 rounded-lg"><Award className="w-4 h-4 text-purple-700" /></span>
              <h4 className="font-bold text-slate-900 text-sm">Publish Social Proof</h4>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Positive testimonials are appended to Google Sheets and auto-published to your website's reviews showcase instantly.
            </p>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Testimonials & Social Proof</h2>
          <p className="text-sm text-slate-500 font-medium">Request client reviews, perform sentiment analysis, and highlight your best ratings</p>
        </div>
        <div className="flex flex-row items-center gap-3">
          <Select
            placeholder="Filter Status"
            style={{ width: 150 }}
            allowClear
            onChange={setFilterStatus}
            options={[
              { value: 'Collected', label: 'Collected' },
              { value: 'Pending', label: 'Pending Request' },
            ]}
          />
          <Button 
            type="primary" 
            icon={<Plus className="w-4 h-4" />} 
            onClick={() => {
              form.resetFields()
              setIsModalOpen(true)
            }}
          >
            Request Testimonial
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
        <DataTable columns={columns} data={filteredReviews} rowKey="id" />
      </div>

      {/* Request Modal */}
      <Modal title="Send Feedback & Review Request" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Form form={form} layout="vertical" onFinish={handleAddReviewRequest}>
          <Form.Item name="customer" label="Customer Name & Company" rules={[{ required: true, message: 'Please input client company name' }]}>
            <Input placeholder="e.g. Rajesh Enterprises (Noida)" />
          </Form.Item>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="phone" label="WhatsApp Number" rules={[{ required: true, message: 'Please enter WhatsApp number' }]}>
              <Input placeholder="e.g. +91 9876543210" />
            </Form.Item>
            <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}>
              <Input placeholder="e.g. rajesh@enterprises.com" />
            </Form.Item>
          </div>
          <Form.Item name="message" label="Custom Greeting Message" rules={[{ required: true, message: 'Please write a message' }]}>
            <Input.TextArea rows={3} placeholder="Please tell us about your experience..." />
          </Form.Item>
          
          <div className="flex justify-between items-center pt-4">
            
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>Request Testimonial</Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </motion.div>
  )
}

export default Testimonials
